import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle, FileText, ArrowRight, Eye, EyeOff, ShieldAlert, AlertCircle } from 'lucide-react';

/**
 * StructuredResponse Component - ConciseFullRenderingLayer
 * 
 * Presentation-layer transformation for concise/full views.
 * Does NOT change reasoning output - only transforms display.
 * 
 * Props:
 *   responseFull: Full structured response (source of truth)
 *   responseConcise: Concise view transformation
 *   viewMetadata: { has_hidden_content, hidden_sections, action_count_full, action_count_concise, is_grouped }
 *   confidenceWarning: Low confidence warning message
 *   confidenceMode: operational | diagnostic | low
 *   defaultView: "concise" (default) | "full"
 */

const ResponseTypeIcon = ({ type }) => {
  const iconClass = "w-5 h-5";
  switch (type) {
    case 'resolution':
      return <CheckCircle className={`${iconClass} text-green-600`} />;
    case 'guidance':
      return <FileText className={`${iconClass} text-blue-600`} />;
    case 'process':
      return <ArrowRight className={`${iconClass} text-purple-600`} />;
    case 'complaint':
      return <AlertTriangle className={`${iconClass} text-orange-600`} />;
    default:
      return <FileText className={`${iconClass} text-gray-600`} />;
  }
};

const SourceTypeBadge = ({ sourceType }) => {
  const styles = {
    internal_kb: 'bg-green-100 text-green-800 border-green-200',
    web: 'bg-blue-100 text-blue-800 border-blue-200',
    hybrid: 'bg-purple-100 text-purple-800 border-purple-200'
  };
  
  const labels = {
    internal_kb: 'Internal KB',
    web: 'Web Source',
    hybrid: 'Hybrid'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${styles[sourceType] || styles.internal_kb}`}>
      {labels[sourceType] || sourceType}
    </span>
  );
};

const ConfidenceBadge = ({ mode, confidence }) => {
  const styles = {
    operational: { bg: 'bg-green-100 text-green-700 border-green-200', label: 'High Confidence' },
    diagnostic: { bg: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Medium Confidence' },
    low: { bg: 'bg-red-100 text-red-700 border-red-200', label: 'Low Confidence' }
  };
  const style = styles[mode] || styles.diagnostic;
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded border ${style.bg}`}>
      {style.label} {confidence ? `(${Math.round(confidence * 100)}%)` : ''}
    </span>
  );
};

const CitationBadge = ({ citation }) => (
  <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-mono bg-slate-100 text-slate-700 rounded border border-slate-200 mx-0.5">
    {citation}
  </span>
);

const ActionStep = ({ step, action, description, isLast, isGrouped }) => (
  <div className={`flex gap-3 ${!isLast ? 'pb-3' : ''}`}>
    <div className={`flex-shrink-0 w-6 h-6 rounded-full ${isGrouped ? 'bg-purple-600' : 'bg-blue-600'} text-white flex items-center justify-center text-xs font-medium`}>
      {step}
    </div>
    <div className="flex-1 pt-0.5">
      <p className="font-medium text-slate-900 text-sm">{action}</p>
      {description && (
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      )}
    </div>
  </div>
);

const CollapsibleSection = ({ title, children, defaultOpen = false, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700 text-sm">{title}</span>
          {badge && <span className="text-xs text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">{badge}</span>}
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-slate-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 py-3 bg-white">
          {children}
        </div>
      )}
    </div>
  );
};

const ConditionsAlert = ({ conditions }) => {
  if (!conditions || conditions.length === 0) return null;
  
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
      <div className="flex gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800 text-sm mb-1">Conditions / Exceptions</p>
          <ul className="space-y-0.5">
            {conditions.map((condition, idx) => (
              <li key={idx} className="text-xs text-amber-700">- {condition}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const NextStepsBlock = ({ nextSteps }) => {
  if (!nextSteps || nextSteps.length === 0) return null;
  
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
      <p className="font-medium text-blue-800 text-sm mb-1">Next Steps</p>
      <ul className="space-y-1">
        {nextSteps.map((step, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <ArrowRight className="w-3 h-3 text-blue-600 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-blue-700">{step}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ViewToggle = ({ viewMode, onToggle, hasHiddenContent, hiddenCount }) => {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
        viewMode === 'full'
          ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
      data-testid="view-mode-toggle"
    >
      {viewMode === 'full' ? (
        <>
          <EyeOff className="w-3.5 h-3.5" />
          <span>Show Concise</span>
        </>
      ) : (
        <>
          <Eye className="w-3.5 h-3.5" />
          <span>Show Full Details</span>
          {hasHiddenContent && hiddenCount > 0 && (
            <span className="bg-slate-200 text-slate-600 px-1 py-0.5 rounded text-[10px]">+{hiddenCount}</span>
          )}
        </>
      )}
    </button>
  );
};

const StructuredResponse = ({ 
  responseFull,
  responseConcise,
  viewMetadata,
  confidenceWarning = null, 
  confidenceMode = null,
  defaultView = "concise"
}) => {
  const [viewMode, setViewMode] = useState(defaultView);
  
  // Use concise or full based on view mode
  const response = viewMode === 'full' ? responseFull : (responseConcise || responseFull);
  const fullResponse = responseFull || {};
  
  if (!response) return null;
  
  const {
    response_type = 'guidance',
    title,
    prerequisites = [],
    direct_answer,
    summary,
    summary_truncated = false,
    root_cause,
    required_actions = [],
    internal_actions = [],
    conditions = [],
    next_steps = [],
    source_type = 'internal_kb',
    source_conflicts = [],
    citations = [],
    confidence
  } = response;
  
  const isGrouped = viewMetadata?.is_grouped || false;
  const hasHiddenContent = viewMetadata?.has_hidden_content || false;
  const hiddenSections = viewMetadata?.hidden_sections || [];
  const isSummaryTruncated = viewMode === 'concise' && (summary_truncated || viewMetadata?.summary_truncated);
  
  const toggleView = () => {
    setViewMode(viewMode === 'full' ? 'concise' : 'full');
  };
  
  return (
    <div className="structured-response space-y-3" data-testid="structured-response">
      {/* Low Confidence Warning - Always visible */}
      {confidenceWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2" data-testid="confidence-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-amber-800">Validation Recommended</p>
            <p className="text-xs text-amber-700">{confidenceWarning}</p>
          </div>
        </div>
      )}
      
      {/* 1. TITLE with View Toggle */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ResponseTypeIcon type={response_type} />
          <h3 className="text-base font-semibold text-slate-900 truncate" data-testid="response-title">
            {title}
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <SourceTypeBadge sourceType={source_type} />
          {confidenceMode && (
            <ConfidenceBadge mode={confidenceMode} confidence={confidence} />
          )}
          {/* View Toggle */}
          {(hasHiddenContent || viewMode === 'full') && (
            <ViewToggle 
              viewMode={viewMode}
              onToggle={toggleView}
              hasHiddenContent={hasHiddenContent}
              hiddenCount={hiddenSections.length}
            />
          )}
        </div>
      </div>
      
      {/* 2. MANDATORY PREREQUISITES — shown at the very top, above actions */}
      {prerequisites.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="prerequisites-block">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert size={16} className="text-amber-600 flex-shrink-0" />
            <span className="text-xs font-semibold text-amber-800 uppercase tracking-wide">
              Mandatory Requirements
            </span>
          </div>
          <div className="space-y-1.5">
            {prerequisites.map((prereq, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="flex-shrink-0 mt-1.5 w-1 h-1 rounded-full bg-amber-500" />
                <p className="text-sm text-amber-900 font-medium leading-snug">{prereq}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 3. DIRECT ANSWER — the specific actionable answer */}
      {direct_answer && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="direct-answer">
          <div className="space-y-1.5">
            {direct_answer.split('\n').filter(line => line.trim()).map((line, idx) => {
              const cleaned = line.replace(/^[-*]\s*/, '').trim();
              const isDecision = /^if\b/i.test(cleaned) || /→|->/.test(cleaned);
              return (
                <div key={idx} className="flex items-start gap-2">
                  <span className={`flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full ${isDecision ? 'bg-emerald-500' : 'bg-emerald-400'}`} />
                  <p className={`text-sm ${isDecision ? 'text-emerald-900 font-medium' : 'text-emerald-800'}`}>
                    {cleaned}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CONFLICT INDICATOR — when sources disagree */}
      {source_conflicts.length > 0 && (
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-2.5" data-testid="conflict-indicator">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-slate-600">
              <span className="font-medium">Source discrepancy detected:</span>
              {source_conflicts.map((conflict, idx) => (
                <p key={idx} className="mt-0.5">{conflict.description}. {conflict.resolution}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 4. SUMMARY - context/explanation (hidden in concise when direct_answer is shown) */}
      {!(viewMode === 'concise' && direct_answer) && (
      <div className="bg-slate-50 rounded-lg p-3" data-testid="response-summary">
        <p className="text-slate-700 text-sm leading-relaxed">{summary}</p>
        {isSummaryTruncated && (
          <button 
            onClick={toggleView}
            className="text-xs text-blue-600 hover:text-blue-700 mt-1 underline"
          >
            Show full summary...
          </button>
        )}
        {citations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {citations.map((citation, idx) => (
              <CitationBadge key={idx} citation={citation} />
            ))}
          </div>
        )}
      </div>
      )}
      
      {/* Citations - shown when summary is hidden (concise with direct_answer) */}
      {viewMode === 'concise' && direct_answer && citations.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {citations.map((citation, idx) => (
            <CitationBadge key={idx} citation={citation} />
          ))}
        </div>
      )}

      {/* Root Cause - Only in Full view (after summary) */}
      {viewMode === 'full' && root_cause && (
        <div className="border-l-3 border-slate-300 pl-3 py-1.5">
          <p className="text-xs font-medium text-slate-500 mb-0.5">Root Cause</p>
          <p className="text-slate-700 text-sm">{root_cause}</p>
        </div>
      )}
      
      {/* 3. REQUIRED ACTIONS - 3-4 grouped in concise, all in full */}
      {required_actions.length > 0 && (
        <div className="space-y-2" data-testid="required-actions">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-slate-800 text-sm">
              {viewMode === 'concise' && isGrouped ? 'Key Actions' : 'Required Actions'}
            </h4>
            {viewMode === 'concise' && viewMetadata?.action_count_full > required_actions.length && (
              <span className="text-xs text-slate-500">
                {required_actions.length} of {viewMetadata.action_count_full} steps
              </span>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            {required_actions.map((action, idx) => (
              <ActionStep
                key={idx}
                step={action.step || idx + 1}
                action={action.action}
                description={action.description}
                isLast={idx === required_actions.length - 1}
                isGrouped={viewMode === 'concise' && isGrouped}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Internal Actions - Only in Full view */}
      {viewMode === 'full' && internal_actions.length > 0 && (
        <CollapsibleSection 
          title="Internal Handling Steps" 
          badge={`${internal_actions.length} steps`}
          defaultOpen={false}
        >
          <ul className="space-y-1.5">
            {internal_actions.map((action, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span className="w-1 h-1 rounded-full bg-slate-400 mt-1.5 flex-shrink-0"></span>
                <div>
                  <p className="font-medium text-slate-700 text-sm">{action.action}</p>
                  {action.description && (
                    <p className="text-xs text-slate-500">{action.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </CollapsibleSection>
      )}
      
      {/* Conditions - Only in Full view */}
      {viewMode === 'full' && <ConditionsAlert conditions={conditions} />}
      
      {/* 4. NEXT STEPS - Max 1-2 in concise, all in full */}
      <NextStepsBlock nextSteps={next_steps} />
      
      {/* Hidden content indicator in concise view */}
      {viewMode === 'concise' && hasHiddenContent && hiddenSections.length > 0 && (
        <div className="text-center py-1">
          <button
            onClick={toggleView}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            <span className="underline">View full details</span>
            <span className="text-slate-400 ml-1">({hiddenSections.length} hidden)</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default StructuredResponse;

export {
  ResponseTypeIcon,
  SourceTypeBadge,
  ConfidenceBadge,
  CitationBadge,
  ActionStep,
  CollapsibleSection,
  ConditionsAlert,
  NextStepsBlock,
  ViewToggle
};
